import type { BulkChange, BulkEditableField, BulkFieldType } from '@/composables/api/useBulkActionApi';
import { formatClockTime } from '@/utils/customPlaylistSchedule';
import { describeBulkChange } from './bulkEditChanges';

/**
 * How the field-by-field bulk edit dialog offers each field. Custom lists use it; schedules are
 * edited with the schedule editor's own form instead (ScheduleBulkEditDialog).
 */

/** A field as the dialog offers it. */
export type BulkDialogField = BulkEditableField;

/** The control a set value needs. */
export type BulkValueControl = BulkFieldType | 'lookup_search';

/** The fields the dialog offers, in alphabetical order of their names. */
export function bulkDialogFields(fields: BulkEditableField[]): BulkDialogField[] {
  return [...fields].sort((first, second) => first.label.localeCompare(second.label));
}

/** The control for a set value of this field. */
export function bulkValueControl(field: BulkDialogField): BulkValueControl {
  if (field.type === 'lookup') return field.lookup?.searchable ? 'lookup_search' : 'lookup';
  return field.type;
}

/** How a set value reads in the confirmation, when the raw value would not read well. */
export function bulkDisplayValue(field: BulkDialogField, value: string): string | undefined {
  if (field.type === 'time') return formatClockTime(value) ?? undefined;
  return undefined;
}

/** One line per change for the confirmation. */
export function bulkChangeSummary(changes: BulkChange[], fields: BulkEditableField[], valueLabels: Record<string, string>): string[] {
  const fieldsByName = new Map(fields.map((field) => [field.field, field]));
  return changes.map((change) => describeBulkChange(change, fieldsByName.get(change.field), valueLabels[change.field]));
}
